import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from './api';

/** Загрузка данных экрана: один запрос, состояние и перезагрузка. */
export function useApi<T>(path: string | null): {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
  setData: (next: T) => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!path) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    api
      .get<T>(path, controller.signal)
      .then((result) => {
        setData(result);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : 'Не удалось загрузить данные');
        setLoading(false);
      });

    return () => controller.abort();
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload, setData };
}

/** Задержка ввода в поиске. */
export function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/**
 * Автосохранение черновика: копит правки и отправляет пачкой.
 * Черновик тренировки сохраняется сам (SPEC §5 T1–T3).
 */
export function useAutosave<T>(
  save: (value: T) => Promise<void>,
  delay = 900,
): { schedule: (value: T) => void; flush: () => Promise<void>; saving: boolean } {
  const [saving, setSaving] = useState(false);
  const pending = useRef<T | null>(null);
  const timer = useRef<number | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const run = useCallback(async () => {
    if (pending.current === null) return;
    const value = pending.current;
    pending.current = null;
    setSaving(true);
    try {
      await saveRef.current(value);
    } finally {
      setSaving(false);
    }
  }, []);

  const schedule = useCallback(
    (value: T) => {
      pending.current = value;
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void run(), delay);
    },
    [delay, run],
  );

  const flush = useCallback(async () => {
    if (timer.current) window.clearTimeout(timer.current);
    await run();
  }, [run]);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  return { schedule, flush, saving };
}
